import { MaterialCommunityIcons } from "@expo/vector-icons";
import { Button, HStack, Icon, Text, VStack } from "native-base";
import React, { useRef, useState } from "react";
import { FlatList, ListRenderItem } from "react-native";

import RoundedIcon from "@/components/atoms/RoundedIcon";
import { useViewport } from "@/contexts/ViewportContext";

import EmptyList from "../EmptyList";

type CarouselProps<T> = {
  data: T[];
  renderItem: ListRenderItem<T>;
  itemLayout: number;
  showViewMoreButton?: boolean;
  onPressViewMore?: () => void;
};

const PassButton = ({ iconName, onPress }) => (
  <Button variant="ghost" colorScheme="white" onPress={onPress}>
    <RoundedIcon boxSize="10" iconSize="md" icon={<MaterialCommunityIcons name={iconName} />} />
  </Button>
);

const Carousel = <T extends unknown>({
  data,
  itemLayout,
  renderItem,
  showViewMoreButton = true,
  onPressViewMore = () => {},
}: CarouselProps<T>) => {
  const { isWebMode } = useViewport();
  const listRef = useRef<FlatList<T>>();

  const [currentIndex, setCurrentIndex] = useState(0);

  const goToPrev = () => {
    listRef?.current?.scrollToIndex({
      index: currentIndex > 0 ? currentIndex - 1 : 0,
      animated: true,
    });
  };

  const goToNext = () => {
    listRef?.current?.scrollToIndex({
      index: currentIndex + 1,
      animated: true,
    });
  };

  if (data.length === 0) {
    return <EmptyList />;
  }

  return (
    <VStack>
      {showViewMoreButton && (
        <Button
          variant="ghost"
          colorScheme="light"
          alignSelf="flex-end"
          p={0}
          rightIcon={
            <Icon
              size="8"
              color="primary.600"
              as={<MaterialCommunityIcons name="chevron-right" />}
            />
          }
          onPress={onPressViewMore}>
          <Text color="primary.600">Ver mais</Text>
        </Button>
      )}
      <HStack justifyContent="space-between">
        {isWebMode && <PassButton iconName="chevron-left-circle" onPress={goToPrev} />}
        <FlatList
          ref={listRef}
          data={data}
          horizontal
          pagingEnabled
          showsHorizontalScrollIndicator={false}
          snapToAlignment="center"
          renderItem={renderItem}
          keyExtractor={(_, index) => index.toString()}
          getItemLayout={(data, index) => ({
            length: itemLayout,
            offset: itemLayout * index * 2,
            index,
          })}
          onScroll={(e) => {
            const offset = e.nativeEvent.contentOffset.x;
            setCurrentIndex(Math.floor(offset / itemLayout / 2));
          }}
        />
        {isWebMode && <PassButton iconName="chevron-right-circle" onPress={goToNext} />}
      </HStack>
    </VStack>
  );
};

export default Carousel;
